import ReactDOM from 'react-dom';

const [
  getInstanceFromNode,
  getNodeFromInstance,
  // getFiberCurrentPropsFromNode,
  // enqueueStateRestore,
  // restoreStateIfNeeded,
  // flushPassiveEffects,
  // IsThisRendererActing,
] = (ReactDOM as any).__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED?.Events || [];

export { getInstanceFromNode, getNodeFromInstance };

export const isToolsCanUse: boolean = (getInstanceFromNode && getNodeFromInstance) || false;

if (!isToolsCanUse) {
  console.error(new Error('Error: ReactDOM.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.Events 对象已失效'));
}

export type WorkTag =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24;

export const FunctionComponentTag = 0;
export const ClassComponentTag = 1;
export const IndeterminateComponentTag = 2; // Before we know whether it is function or class
export const HostRootTag = 3; // Root of a host tree. Could be nested inside another node.
export const HostPortalTag = 4; // A subtree. Could be an entry point to a different renderer.
export const HostComponentTag = 5; // react 中原生元素fiber节点的 tag
export const HostTextTag = 6; // react 中文本fiber节点的 tag
export const FragmentTag = 7;
export const ModeTag = 8;
export const ContextConsumerTag = 9;
export const ContextProviderTag = 10;
export const ForwardRefTag = 11; // react 中forwardRef fiber节点的 tag
export const ProfilerTag = 12;
export const SuspenseComponentTag = 13;
export const MemoComponentTag = 14;
export const SimpleMemoComponentTag = 15;
export const LazyComponentTag = 16;
export const IncompleteClassComponentTag = 17;
export const DehydratedFragmentTag = 18;
export const SuspenseListComponentTag = 19;
export const FundamentalComponentTag = 20;
export const ScopeComponentTag = 21;
export const BlockTag = 22;
export const OffscreenComponentTag = 23;
export const LegacyHiddenComponentTag = 24;


// Fiber 是在需要完成或已经完成的组件上的工作。 每个组件可以有多个。
export type Fiber = {
  // 这些第一个字段在概念上是实例的成员。 这曾经被拆分成一个单独的类型并与其他 Fiber 字段相交，但在 Flow 修复其交叉错误之前，我们已将它们合并为一个单一类型。

  // 实例在组件的所有版本之间共享。 
  // 我们可以轻松地将其分解为一个单独的对象，以避免将太多内容复制到树的替代版本中。 
  // 我们暂时将其放在单个对象上，以最大程度地减少初始渲染期间创建的对象数量。

  // 标记节点的身份.
  tag: WorkTag,

  // 节点的唯一标识
  key: null | string,

  // element.type 的值，用于在此孩子的协调期间保留身份
  elementType: any,

  // The resolved function/class/ associated with this fiber.
  type: any,

  // The local state associated with this fiber.
  stateNode: any,

  // Conceptual aliases
  // parent : Instance -> return The parent happens to be the same as the
  // return fiber since we've merged the fiber and instance.

  // Remaining fields belong to Fiber

  // The Fiber to return to after finishing processing this one.
  // This is effectively the parent, but there can be multiple parents (two)
  // so this is only the parent of the thing we're currently processing.
  // It is conceptually the same as the return address of a stack frame.
  return: Fiber | null,

  // Singly Linked List Tree Structure.
  child: Fiber | null,
  sibling: Fiber | null,
  index: number,

  // The ref last used to attach this node.
  // I'll avoid adding an owner field for prod and model that as functions.
  ref: any,

  // Input is the data coming into process this fiber. Arguments. Props.
  pendingProps: any, // This type will be more specific once we overload the tag.
  memoizedProps: any, // The props used to create the output.

  // A queue of state updates and callbacks.
  updateQueue: any,

  // The state used to create the output
  memoizedState: any,

  // Dependencies (contexts, events) for this fiber, if it has any
  dependencies: any,

  // Bitfield that describes properties about the fiber and its subtree. E.g.
  // the ConcurrentMode flag indicates whether the subtree should be async-by-
  // default. When a fiber is created, it inherits the mode of its
  // parent. Additional flags can be set at creation time, but after that the
  // value should remain unchanged throughout the fiber's lifetime, particularly
  // before its child fibers are created.
  mode: number,

  // Effect
  flags: number,
  subtreeFlags: number,
  deletions: Array<Fiber> | null,

  // Singly linked list fast path to the next fiber with side-effects.
  nextEffect: Fiber | null,

  // The first and last fiber with side-effect within this subtree. This allows
  // us to reuse a slice of the linked list when we reuse the work done within
  // this fiber.
  firstEffect: Fiber | null,
  lastEffect: Fiber | null,

  lanes: number,
  childLanes: number,

  // This is a pooled version of a Fiber. Every fiber that gets updated will
  // eventually have a pair. There are cases when we can clean up pairs to save
  // memory if we need to.
  alternate: Fiber | null,

  // Time spent rendering this Fiber and its descendants for the current update.
  // This tells us how well the tree makes use of sCU for memoization.
  // It is reset to 0 each time we render and only updated when we don't bailout.
  // This field is only set when the enableProfilerTimer flag is enabled.
  actualDuration?: number,

  // If the Fiber is currently active in the "render" phase,
  // This marks the time at which the work began.
  // This field is only set when the enableProfilerTimer flag is enabled.
  actualStartTime?: number,

  // Duration of the most recent render time for this Fiber.
  // This value is not updated when we bailout for memoization purposes.
  // This field is only set when the enableProfilerTimer flag is enabled.
  selfBaseDuration?: number,

  // Sum of base times for all descendants of this Fiber.
  // This value bubbles up during the "complete" phase.
  // This field is only set when the enableProfilerTimer flag is enabled.
  treeBaseDuration?: number,
};

// 遍历 react fiber 树
export function workLoop (fiberRoot: Fiber, fiberHandler: (fiber: Fiber) => any) {
  let root = fiberRoot;
  let current = fiberRoot;
  while (true) {
    let child = fiberHandler?.(current);
    if (child) {
      current = child;
      continue;
    }
    if (current === null) {
      return;
    }
    while (!current.sibling) {
      if (!current.return || current.return === root) {
        return;
      }
      current = current.return;
    }
    current = current.sibling;
  }
}